import React, { useState, useEffect } from 'react';
import { Grid, Box } from '@mui/material';
import { Checkbox, Button as MUXButton, useKeyPress } from '@manulife/mux';
import { TextLink } from 'application/Components/UI/Input/TextLink';
import Button from 'application/Components/UI/Buttons/Button';
import { Separator } from 'application/Components/UI/Separator';
import { selectAppSettings } from 'store/reducers/appSettings/appSettings.slice';
import { useDispatch, useSelector } from 'react-redux';
import { storageNames } from 'constants/storageNames';
import storage from 'utils/storage';
import useAdobeDataLayer from 'hooks/useAdobeDataLayer';
import {
  usernameEnteredAction,
  usernameEnteredActionRps,
} from 'store/reducers/login/login.action';
import {
  loginSelector,
  onChange,
  setError,
} from 'store/reducers/login/login.slice';
import { enterUsernameStyles as styles } from '../../../../styles/login.styles';
import useAppids from 'hooks/useAppids';
import Input from 'application/Components/UI/Input';

const EnterUsername = () => {
  const dispatch = useDispatch();
  const adobeDataLayer = useAdobeDataLayer();
  const { fuidURL, registrationURL } = useSelector(selectAppSettings);
  // const { username: sesionUsername } = useSelector(selectSession);
  const { username: usernameRdx } = useSelector(loginSelector);
  const { isRpsAppid, isSprntAppid } = useAppids();

  const username = usernameRdx.value;

  const [remember, setRemember] = useState(
    storage.local.get(storageNames.REMEMBER_USERNAME_ACTIVE) || false,
  );
  const [btnError, setBtnError] = useState(false);

  // Submit when user presses enter key
  const enterPress = useKeyPress('Enter');
  useEffect(() => {
    if (enterPress) clickSubmit();
  }, [enterPress]);

  // Updates username value when user types
  const handleUsernameChange = (newValue) => {
    dispatch(onChange({ username: newValue, password: '' }));
    dispatch(setError({ username: false, password: false }));
    setBtnError(false);
  };

  // Remembers the username when checkbox is clicked
  const handleRememberUsername = (shouldRemember) => {
    if (shouldRemember !== remember) setRemember(shouldRemember);

    if (shouldRemember) {
      storage.local.set(storageNames.REMEMBER_USERNAME_VALUE, username.trim());
      storage.local.set(storageNames.REMEMBER_USERNAME_ACTIVE, shouldRemember);
    } else {
      storage.local.remove(storageNames.REMEMBER_USERNAME_VALUE);
      storage.local.remove(storageNames.REMEMBER_USERNAME_ACTIVE);
    }
  };

  // Handles form submit
  const clickSubmit = () => {
    // trim input username value and pass trimmed username to store
    const finalUsername = username.trim();
    handleUsernameChange(finalUsername);

    handleRememberUsername(remember);

    if (!finalUsername) {
      dispatch(setError({ username: 'Your username is required' }));
      setBtnError('Please add your username before continuing');
      return;
    }
    adobeDataLayer.loginStartDL();

    if (isRpsAppid) {
      dispatch(usernameEnteredActionRps());
      return;
    }
    dispatch(usernameEnteredAction());
  };

  const onClickForgotUsername = (e) => {
    adobeDataLayer.onClickLink('Forgot username', fuidURL);
  };

  const onClickRegister = (e) => {
    adobeDataLayer.onClickLink('Register', registrationURL);
  };

  const showRegisterBtn = () => {
    let show = true;
    if (isSprntAppid) {
      show = false;
    }

    return !!registrationURL && show;
  };

  const onFocus = () => {
    adobeDataLayer.loginStartDL();
  };

  const hideForgotUsername = isSprntAppid;

  return (
    <Grid sx={styles.container}>
      <Input
        label="Username"
        placeholder="Your username"
        value={username}
        error={usernameRdx.error}
        onChange={handleUsernameChange}
        onFocus={onFocus}
      />
      <Box sx={styles.checkbox}>
        <Checkbox
          id="rememberCheckbox"
          checked={remember}
          onClick={() => handleRememberUsername(!remember)}
        >
          Remember username
        </Checkbox>
      </Box>
      {hideForgotUsername ? null : (
        <TextLink
          preText=""
          linkText="Forgot username"
          postText=""
          href={fuidURL}
          onClick={onClickForgotUsername}
          sx={styles.textLinkSx}
        />
      )}
      <Button
        sx={styles.button}
        onClick={clickSubmit}
        loading={usernameRdx.loading}
        error={btnError}
      >
        Continue
      </Button>
      {showRegisterBtn() && (
        <>
          <Separator text="or" />

          <a
            href={registrationURL}
            style={styles.buttonWrapper}
            onClick={onClickRegister}
          >
            <Button variant={MUXButton.VARIANT.SECONDARY} sx={styles.button}>
              Register
            </Button>
          </a>
        </>
      )}
    </Grid>
  );
};

export default EnterUsername;
